import { useState, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Snackbar,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useLogin } from 'Contexts/AuthProvider/AuthProvider';
import InformationDialog from 'Dialogs/Information/Information';
import { useDialog } from 'Contexts/Modals/ModalProvider';
import { ipayNet, newServer } from 'constants/constants';
import { useNavigate, useLocation } from 'react-router-dom';
import { HomeConfig } from 'Config/home.config';
import HttpsIcon from '@mui/icons-material/Https';

const loginWith2FA = true;

const AuthForm = ({ isModal: _isModal }: any) => {
  const { setUser } = useLogin();
  const { openDialog } = useDialog();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('error');
  const [loading, setLoading] = useState(false);
  const [_2fa, set2fa] = useState();
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  const showNotification = useCallback((message: string, severity: 'success' | 'error' = 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  }, []);

  const handleForgotPasswordOpen = async () => {
    openDialog('reset-password', InformationDialog as any, {
      title: 'Reset Password',
      description: 'Enter the email address associated with your account and we will send you a link to reset your password.',
      icon: <HttpsIcon />,
      input: forgotPasswordEmail,
      setInput: (value: string) => setForgotPasswordEmail(value as any),
      inputPlaceholder: 'Enter email',
      cancelText: 'Cancel',
      onConfirm: (value: string) => handlePasswordReset(value, ''),
    });
  };

  const showModal2fa = () => {
    openDialog('information', InformationDialog as any, {
      title: 'Enter Verification Code',
      description: 'We sent a 6-digit code to your email. Enter it below to complete login.',
      icon: <HttpsIcon />,
      input: _2fa,
      setInput: set2fa,
      inputPlaceholder: 'Enter 6-digit code',
      cancelText: 'Cancel',
      onConfirm: validate2FA,
    });
  };

  const validate2FA = async (code: string): Promise<boolean> => {
    if (!code) {
      showNotification('Please enter the verification code');
      return false;
    }
    
    try {
      const userId = sessionStorage.getItem('otpUserId');
      const res = await fetch(`${newServer}login/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ otp: code, userId: userId ? parseInt(userId) : undefined, remember_me: rememberMe }),
      });

      if (res.status === 200) {
        const data = await res.json();
        if (data?.token && data.token.error === undefined) {
          setUser({ email, token: data.token, user_id: data?.user.id, uuid: data?.user.uuid });
          return true; // Éxito - cerrar modal
        } else {
          showNotification(data?.token?.info?.message || 'Invalid code. Please try again.');
          return false; // Error - mantener modal abierto
        }
      }

      const errorText = await res.text();
      showNotification(errorText || 'Invalid code. Please try again.');
      return false; // Error - mantener modal abierto
    } catch (error) {
      console.error('validate2FA error:', error);
      showNotification('Error validating code. Please try again.');
      return false; // Error - mantener modal abierto
    }
  };

  // React no usa subdominios, usa el principal
  const CHEMLOG_SUBDOMAIN = 'caneg';

  const handleLogin = async () => {
    if (loading || !email || !password) return;
    
    setLoading(true);
    setAlertMessage('');
    
    try {
      const response = await fetch(`${newServer}login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': ipayNet.apiToken,
          'subdomain': CHEMLOG_SUBDOMAIN,
        },
        body: JSON.stringify({ email, password, remember_me: rememberMe }),
      });

      setLoading(false);

      if (response.status === 429) {
        showNotification('Too many attempts. Please wait and try again later.');
        return;
      }

      if (response.status === 200) {
        const text = await response.text();
        
        try {
          const data = JSON.parse(text);
          
          // Si viene userId, significa que requiere OTP
          if (loginWith2FA && data?.userId) {
            console.log('OTP requerido para userId:', data.userId);
            sessionStorage.setItem('otpUserId', data.userId);
            showModal2fa();
            return;
          }
          
          // Login exitoso sin 2FA o respuesta directa con token
          if (data?.token && data.token.error === undefined) {
            setUser({ email, token: data.token, user_id: data?.user_id || data?.user?.id, uuid: data?.uuid });
          } else if (data?.error) {
            setAlertMessage(data?.message || 'Login failed');
            showNotification('Login failed. Please check your credentials.');
          } else {
            setAlertMessage(data?.message || 'Login failed');
            showNotification('Login failed. Please check your credentials.');
          }
        } catch {
          // Respuesta de texto plano 'OK' (compatibilidad)
          if (loginWith2FA && text === 'OK') {
            showModal2fa();
            return;
          }
          setAlertMessage('Login failed');
        }
      } else {
        const textMessage = await response.text();
        setAlertMessage(textMessage || 'Invalid credentials');
        showNotification('Login failed. Please check your credentials.');
      }
    } catch (error) {
      setLoading(false);
      console.error('handleLogin', error);
      setAlertMessage('Connection error. Please try again.');
      showNotification('Connection error. Please try again.');
    }
  };

  const handleRegister = async () => {
    if (loading || !email || !password) return;
    
    setLoading(true);
    setAlertMessage('');

    try {
      const response = await fetch(`${newServer}users/simple`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': ipayNet.apiToken,
        },
        body: JSON.stringify({ 
          email, 
          password, 
          subDomain: 'subdominio1', 
          subDomainName: 'Shelter 1' 
        }),
      });

      setLoading(false);

      if (response.status === 200) {
        const text = await response.text();
        if (loginWith2FA && text === 'OK') {
          showModal2fa();
          return;
        }

        try {
          const data = JSON.parse(text);
          if (data?.token) {
            showNotification('Account created successfully!', 'success');
            setUser({ email, token: data.token, user_id: data?.user_id, uuid: data?.uuid });
          }
        } catch {
          setAlertMessage('Registration failed');
        }
      } else {
        const textMessage = await response.text();
        setAlertMessage(textMessage || 'Registration failed');
        showNotification('Registration failed. Please try again.');
      }
    } catch (error) {
      setLoading(false);
      console.error('handleRegister', error);
      setAlertMessage('Connection error. Please try again.');
    }
  };

  const handlePasswordReset = async (emailInput: string, subdomain: any): Promise<boolean> => {
    // Validar que el email no esté vacío
    if (!emailInput || !emailInput.trim()) {
      showNotification('Please enter your email address.');
      return false;
    }

    // Validar formato de email básico
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailInput.trim())) {
      showNotification('Please enter a valid email address.');
      return false;
    }

    try {
      const res = await fetch(`${newServer}forgot-passwords/request`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'x-api-key': ipayNet.apiToken,
          'subdomain': subdomain || 'default',
        },
        body: JSON.stringify({ email: emailInput.trim() }),
      });
      
      if (res.status === 200) {
        showNotification('Password reset link sent to your email!', 'success');
        return true; // Éxito - cerrar modal
      } else {
        const errorText = await res.text();
        showNotification(errorText || 'Error resetting password. Please try again.');
        return false; // Error - mantener modal abierto
      }
    } catch (error) {
      console.error('handlePasswordReset error:', error);
      showNotification('Connection error. Please try again.');
      return false; // Error - mantener modal abierto
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && email && password && !loading) {
      handleLogin();
    }
  };

  const isFormValid = email.length > 0 && password.length > 0;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f1f5f9',
        p: 2,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 400,
          bgcolor: 'white',
          borderRadius: 3,
          p: { xs: 3, sm: 4 },
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
          border: '1px solid #e2e8f0',
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              bgcolor: '#E91E8C',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <Lock sx={{ color: 'white', fontSize: 28 }} />
          </Box>
          <Typography
            variant="h5"
            sx={{
              color: '#1e293b',
              fontWeight: 700,
              mb: 0.5,
            }}
          >
            Welcome Back
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: '#64748b' }}
          >
            Sign in to {HomeConfig.merchatName} Platform
          </Typography>
        </Box>

        {/* Alert */}
        {alertMessage && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2, 
              borderRadius: 2,
            }}
          >
            {alertMessage}
          </Alert>
        )}

        {/* Form */}
        <Box component="form" onKeyDown={handleKeyDown}>
          <TextField
            label="Email"
            variant="outlined"
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            autoComplete="email"
            autoFocus
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '& fieldset': { borderColor: '#e2e8f0' },
                '&:hover fieldset': { borderColor: '#cbd5e1' },
                '&.Mui-focused fieldset': { borderColor: '#E91E8C' },
              },
              '& .MuiInputLabel-root': { 
                color: '#64748b',
                '&.Mui-focused': { color: '#E91E8C' },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email sx={{ color: '#94a3b8', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="Password"
            variant="outlined"
            fullWidth
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            autoComplete="current-password"
            sx={{
              mb: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '& fieldset': { borderColor: '#e2e8f0' },
                '&:hover fieldset': { borderColor: '#cbd5e1' },
                '&.Mui-focused fieldset': { borderColor: '#E91E8C' },
              },
              '& .MuiInputLabel-root': { 
                color: '#64748b',
                '&.Mui-focused': { color: '#E91E8C' },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: '#94a3b8', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                    sx={{ color: '#94a3b8' }}
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Remember Me & Forgot Password */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  size="small"
                  sx={{
                    color: '#94a3b8',
                    '&.Mui-checked': { color: '#E91E8C' },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>
                  Remember me
                </Typography>
              }
            />
            <Typography
              variant="body2"
              onClick={handleForgotPasswordOpen}
              sx={{
                color: '#E91E8C',
                cursor: 'pointer',
                fontSize: '0.8rem',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Forgot password?
            </Typography>
          </Box>

          {/* Buttons */}
          <Button
            fullWidth
            variant="contained"
            onClick={handleLogin}
            disabled={!isFormValid || loading}
            sx={{
              py: 1.5,
              borderRadius: 2,
              bgcolor: '#E91E8C',
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem',
              boxShadow: '0 2px 8px rgba(233, 30, 140, 0.25)',
              '&:hover': { 
                bgcolor: '#c41874',
                boxShadow: '0 4px 12px rgba(233, 30, 140, 0.35)',
              },
              '&:disabled': { 
                bgcolor: '#e2e8f0', 
                color: '#94a3b8',
                boxShadow: 'none',
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Sign In'
            )}
          </Button>

          {location.pathname !== '/backoffice' && (
            <Button
              fullWidth
              variant="outlined"
              onClick={handleRegister}
              disabled={!isFormValid || loading}
              sx={{
                mt: 1.5,
                py: 1.5,
                borderRadius: 2,
                borderColor: '#e2e8f0',
                color: '#1e293b',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
                '&:hover': { 
                  borderColor: '#E91E8C',
                  bgcolor: 'rgba(233, 30, 140, 0.05)',
                },
                '&:disabled': { 
                  borderColor: '#e2e8f0', 
                  color: '#94a3b8' 
                },
              }}
            >
              Create Account
            </Button>
          )}
        </Box>

        {/* Footer */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'center',
            mt: 3,
            color: '#94a3b8',
            fontSize: '0.7rem',
            lineHeight: 1.6,
          }}
        >
          By signing in, you agree to our{' '}
          <Box
            component="span"
            onClick={() => navigate('/terms')}
            sx={{
              color: '#E91E8C',
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Terms
          </Box>{' '}
          and{' '}
          <Box
            component="span"
            onClick={() => navigate('/policies')}
            sx={{
              color: '#E91E8C',
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Privacy Policy
          </Box>
        </Typography>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity}
          sx={{ borderRadius: 2 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AuthForm;
